import React, { useState, useEffect, useCallback } from 'react';

import * as Page from 'components/page';
import { RHP } from 'providers';

export function StartupStep11({ useRankingStartup, t }) {
    const {
        vars,
        getStartupRankingApplication,
        postStartupAuditEnrollment,
    } = useRankingStartup;
    const { application } = vars;
    const { steps } = application;

    const [localEnrollment, setLocalEnrollment] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);

    // Persisted state wins when it's terminal (confirmed) — webhooks may
    // have updated it while the tab was idle. Otherwise use the most
    // recent API response from this session (covers the open-startup
    // auto-confirm case, which is never persisted by the backend).
    const persisted = application?.auditEnrollment;
    const enrollment =
        persisted?.status === 'confirmed'
            ? persisted
            : localEnrollment || persisted || { status: 'pending' };

    const refresh = useCallback(() => {
        getStartupRankingApplication().catch(() => {});
    }, [getStartupRankingApplication]);

    const enroll = useCallback(async () => {
        setLoading(true);
        setErrorMsg(null);
        try {
            const redirectUrl = window.location.href;
            const result = await postStartupAuditEnrollment({ redirectUrl });
            setLocalEnrollment(result);
        } catch (err: any) {
            console.error(err);
            setErrorMsg(
                err?.response?.data?.message ||
                err?.message ||
                t('StartupStep11.errorGeneric')
            );
        } finally {
            setLoading(false);
        }
    }, [postStartupAuditEnrollment, t]);

    useEffect(() => {
        if (enrollment.status === 'confirmed') return;
        if (enrollment.status === 'processing' && enrollment.paymentLink) return;
        enroll();
    }, [enrollment.status, enrollment.paymentLink, enroll]);

    useEffect(() => {
        const onVisible = () => {
            if (document.visibilityState === 'visible') refresh();
        };
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('focus', refresh);
        return () => {
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('focus', refresh);
        };
    }, [refresh]);

    if (!steps || steps.step10 !== true) {
        RHP.replace('/ranking/latam/startup/');
        return null;
    }

    const renderStatus = () => {
        if (enrollment.status === 'confirmed') {
            const isEarlyStage =
                enrollment.isComputed === true ||
                enrollment.note === 'open-startup-auto';
            return (
                <div style={{ padding: '1rem', background: '#e6f7ed', borderRadius: 4 }}>
                    <strong>
                        {t(
                            isEarlyStage
                                ? 'StartupStep11.statusEarlyStageConfirmedTitle'
                                : 'StartupStep11.statusScaleupConfirmedTitle'
                        )}
                    </strong>
                    <p style={{ margin: '0.5rem 0 0' }}>
                        {t(
                            isEarlyStage
                                ? 'StartupStep11.statusEarlyStageConfirmedDescr'
                                : 'StartupStep11.statusScaleupConfirmedDescr'
                        )}
                    </p>
                </div>
            );
        }
        if (enrollment.status === 'processing' && enrollment.paymentLink) {
            return (
                <div style={{ padding: '1rem', background: '#fff8e1', borderRadius: 4 }}>
                    <strong>{t('StartupStep11.statusProcessingTitle')}</strong>
                    <p style={{ margin: '0.5rem 0 0.75rem' }}>
                        {t('StartupStep11.statusProcessingDescr')}
                    </p>
                    <a href={enrollment.paymentLink} className="btn btn-primary">
                        {t('StartupStep11.payButton')}
                    </a>
                </div>
            );
        }
        if (errorMsg || enrollment.status === 'failed') {
            return (
                <div style={{ padding: '1rem', background: '#fdecea', borderRadius: 4 }}>
                    <strong>{t('StartupStep11.statusFailedTitle')}</strong>
                    <p style={{ margin: '0.5rem 0 0.75rem' }}>
                        {errorMsg || t('StartupStep11.statusFailedDescr')}
                    </p>
                    <button
                        type="button"
                        onClick={enroll}
                        disabled={loading}
                        className="btn btn-primary"
                    >
                        {t('StartupStep11.tryAgain')}
                    </button>
                </div>
            );
        }
        return (
            <div style={{ padding: '1rem', background: '#f2f2f2', borderRadius: 4 }}>
                <strong>{t('StartupStep11.processing')}</strong>
            </div>
        );
    };

    return (
        <Page.Page
            name="StartupStep11"
            back="/ranking/latam/startup"
            customBackText={t('glossary:backToRanking')}
        >
            <Page.Title>{t('StartupStep11.title')}</Page.Title>
            <Page.Descr>{t('StartupStep11.description')}</Page.Descr>

            <p style={{ fontSize: '0.9em', color: '#666', marginTop: 12 }}>
                {t('StartupStep11.priceNote')}
            </p>

            <h3 style={{ marginTop: 24 }}>{t('StartupStep11.statusSectionTitle')}</h3>
            {renderStatus()}

            <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
                <Page.Buttn
                    style={{ width: '20%' }}
                    // @ts-ignore
                    to="/ranking/latam/startup/step10"
                >
                    <span className="icon far fa-long-arrow-left" aria-hidden="true" />
                    <span className="text">{t('glossary:back')}</span>
                </Page.Buttn>

                <Page.Buttn
                    style={{ width: '80%' }}
                    // @ts-ignore
                    to="/ranking/latam/startup"
                >
                    <span className="text">{t('glossary:backToRanking')}</span>
                </Page.Buttn>
            </div>
        </Page.Page>
    );
}
